import pako from "pako"
import { Buffer } from "buffer"
import consola from "consola"
import { IRegisterDocumentProps, IRegisterMapProps, IRegisterStreamProps, TRingioSyncDocument, TRingioSyncMap, TRingioSyncStream } from "@/sync/syncTypes"
import { AccountDocumentHandler } from "@/sync/handlers/documents/account.handler"
import { ClusterDocumentHandler } from "@/sync/handlers/documents/cluster.handler"
import { SmartViewDocumentHandler } from "@/sync/handlers/documents/smartView.handler"
import { UserPreferencesDocumentHandler } from "@/sync/handlers/documents/userPreferences.handler"
import { UserStatusDocumentHandler } from "@/sync/handlers/documents/userStatus.handler"
import { RecentCallsStreamHandler } from "@/sync/handlers/streams/recentCalls.handler"
import { ContactsMapHandler } from "@/sync/handlers/maps/contacts.handler"
import { LiveCallsMapHandler } from "@/sync/handlers/maps/liveCalls.handler"
import { LastRingDocumentHandler } from "@/sync/handlers/documents/lastRing.handler"

/**
 * Inflates a base64-encoded data object.
 * If the data object is not deflated, it returns the original data object.
 * If the data object is deflated, it inflates it and returns the inflated object.
 * If an error occurs during the inflation process, it logs the error and returns an empty object.
 *
 * @param data - The base64-encoded data object to inflate.
 * @returns The inflated data object or an empty object if an error occurs.
 */
export function inflateBase64(data: any): Record<string, unknown> {
  if (!data?.deflated) {
    return data || {}
  }

  try {
    let t1 = data.deflated
    let t2 = Buffer.from(t1, "base64")
    let t3 = pako.inflate(t2, { to: "string" })
    let t4 = JSON.parse(t3)

    return t4 as Record<string, unknown>
  } catch (err) {
    consola.error(`Error inflating data: ${err}`, JSON.stringify(data))
    return {}
  }
}

/**
 * Inflates a map item.
 * @param item - The item to be inflated.
 * @returns The inflated item.
 */
export function inflateMapItem(item: any) {
  return inflateBase64(item)
}

interface ILoadDocumentProps {
  documentName: string
  type: TRingioSyncDocument
}
async function loadDocument({ documentName, type }: ILoadDocumentProps) {
  const syncClient = window.ringioFrontV3.twilio.sync.client!

  if (window.ringioFrontV3.twilio.sync.initialization.documents[type]) {
    // TODO: Maybe we want to restart the connection?
    // unloadSyncConnection(documentName)
    consola.warn(`${documentName} is already loaded!`)
    return
  }

  let handler: IRegisterDocumentProps | undefined = undefined
  switch (type) {
    case "account":
      handler = AccountDocumentHandler(documentName)
      break
    case "cluster":
      handler = ClusterDocumentHandler(documentName)
      break
    case "smartView":
      handler = SmartViewDocumentHandler(documentName)
      break
    case "userPreferences":
      handler = UserPreferencesDocumentHandler(documentName)
      break
    case "userStatus":
      handler = UserStatusDocumentHandler(documentName)
      break
    case "lastRing":
      handler = LastRingDocumentHandler(documentName)
      break
    default:
      break
  }

  if (!handler) {
    consola.error(`No handler found for ${documentName}!`)
    return
  }

  const syncDoc = await syncClient.document!(documentName)

  syncDoc.on("updated", (e: any) => {
    const _item: Record<string, unknown> = e?.data?.deflated ? inflateMapItem(e?.data) : e?.data

    handler.onDocumentUpdated(_item, e)
  })

  syncDoc.on("failure", (e) => {
    handler.onFailure(e)
  })

  syncDoc.on("removed", () => {
    handler.onRemoved()
    syncDoc.removeAllListeners()

    window.ringioFrontV3.twilio.sync.initialization.documents[type] = undefined
  })

  window.ringioFrontV3.twilio.sync.initialization.documents[type] = syncDoc

  if (handler.onConnected) {
    handler.onConnected()
  }

  consola.debug(`Connected sync to ${documentName}`)
}

interface ILoadMapProps {
  mapName: string
  type: TRingioSyncMap
}
async function loadMap({ mapName, type }: ILoadMapProps) {
  const syncClient = window.ringioFrontV3.twilio.sync.client!
  // Check if the doc is already in syncConnections and if it is
  // there, unregister it and its listeners first
  if (window.ringioFrontV3.twilio.sync.initialization.maps[type]) {
    // TODO: Maybe we want to restart the connection?
    // unloadSyncConnection(documentName)
    consola.warn(`${mapName} is already loaded!`)
    return
  }

  let handler: IRegisterMapProps | undefined = undefined
  switch (type) {
    case "contacts":
      handler = ContactsMapHandler(mapName)
      break
    case "liveCalls":
      handler = LiveCallsMapHandler(mapName)
      break
    default:
      break
  }

  if (!handler) {
    consola.error(`No handler found for ${mapName}!`)
    return
  }

  const syncMap = await syncClient.map!(mapName)

  syncMap.on("itemAdded", (e: any) => {
    const _item: Record<string, unknown> = e?.item?.data?.deflated ? inflateMapItem(e?.item?.data) : e?.item?.data

    // @ts-ignore - Remove once we add some map handlers
    handler.onItemAdded(_item, e)
  })

  syncMap.on("itemUpdated", (e: any) => {
    const _item: Record<string, unknown> = e?.item?.data?.deflated ? inflateMapItem(e?.item?.data) : e?.item?.data

    // @ts-ignore - Remove once we add some map handlers
    handler.onItemUpdated(_item, e)
  })

  syncMap.on("itemRemoved", (e: any) => {
    const _item: Record<string, unknown> = e?.item?.data?.deflated ? inflateMapItem(e?.item?.data) : e?.item?.data

    // @ts-ignore - Remove once we add some map handlers
    handler.onItemRemoved(_item, e)
  })

  syncMap.on("failure", (e) => {
    // @ts-ignore - Remove once we add some map handlers
    handler.onFailure(e)
  })

  syncMap.on("removed", () => {
    // @ts-ignore - Remove once we add some map handlers
    handler.onRemoved()
    syncMap.removeAllListeners()

    window.ringioFrontV3.twilio.sync.initialization.maps[type] = undefined
  })

  window.ringioFrontV3.twilio.sync.initialization.maps[type] = syncMap

  // @ts-ignore - Remove once we add some map handlers
  if (handler.onConnected) {
    // @ts-ignore - Remove once we add some map handlers
    handler.onConnected()
  }

  consola.debug(`Connected sync to ${mapName}`)
}

interface ILoadStreamProps {
  streamName: string
  type: TRingioSyncStream
}
async function loadStream({ streamName, type }: ILoadStreamProps) {
  const syncClient = window.ringioFrontV3.twilio.sync.client!

  // Check if the doc is already in syncConnections and if it is
  // there, unregister it and its listeners first
  if (window.ringioFrontV3.twilio.sync.initialization.streams[type]) {
    // TODO: Maybe we want to restart the connection?
    // unloadSyncConnection(documentName)
    consola.warn(`${streamName} is already loaded!`)
    return
  }

  let handler: IRegisterStreamProps | undefined = undefined
  switch (type) {
    case "recentCalls":
      handler = RecentCallsStreamHandler(streamName)
      break
    default:
      break
  }

  if (!handler) {
    consola.error(`No handler found for ${streamName}!`)
    return
  }

  const syncStream = await syncClient.stream!(streamName)

  syncStream.on("messagePublished", (e: any) => {
    const _item: Record<string, unknown> = e?.message?.data?.deflated ? inflateMapItem(e?.message?.data) : e?.message?.data

    handler.onMessagePublished(_item, e)
  })

  syncStream.on("failure", (e) => {
    handler.onFailure(e)
  })

  syncStream.on("removed", () => {
    handler.onRemoved()
    syncStream.removeAllListeners()

    window.ringioFrontV3.twilio.sync.initialization.streams[type] = undefined
  })

  window.ringioFrontV3.twilio.sync.initialization.streams[type] = syncStream

  if (handler.onConnected) {
    handler.onConnected()
  }

  consola.debug(`Connected sync to ${streamName}`)
}

interface ILoadSync {
  key: string
  name: string
}

export async function loadSync({ key, name }: ILoadSync) {
  if (!window.ringioFrontV3.twilio.sync.client || window.ringioFrontV3.twilio.sync.client?.connectionState !== "connected") {
    consola.warn("Sync client is not ready!")
    return
  }

  switch (key) {
    case "account":
      // No need to await
      loadDocument({
        documentName: name,
        type: "account",
      })
      break
    // case "departmentStats":
    //   // No need to await
    //   loadDocument({
    //     documentName: name,
    //     type: "departmentStats",
    //   })
    //   break
    // case "dialerList":
    //   // No need to await
    //   loadDocument({
    //     documentName: name,
    //     type: "dialerList",
    //   })
    //   break
    // case "intention":
    //   // No need to await
    //   loadDocument({
    //     documentName: name,
    //     type: "intention",
    //   })
    //   break
    case "lastRing":
      // No need to await
      loadDocument({
        documentName: name,
        type: "lastRing",
      })
      break
    case "liveCalls":
      // No need to await
      loadMap({
        mapName: name,
        type: "liveCalls",
      })
      break
    // case "paymentStatus":
    //   // No need to await
    //   loadDocument({
    //     documentName: name,
    //     type: "paymentStatus",
    //   })
    //   break
    case "recentCalls":
      // No need to await
      loadStream({
        streamName: name,
        type: "recentCalls",
      })
      break
    // case "roster":
    //   // No need to await
    //   loadDocument({
    //     documentName: name,
    //     type: "roster",
    //   })
    //   break
    case "smartView":
      // No need to await
      loadDocument({
        documentName: name,
        type: "smartView",
      })
      break
    case "userPreferences":
      // No need to await
      loadDocument({
        documentName: name,
        type: "userPreferences",
      })
      break
    // case "userStats":
    //   // No need to await
    //   loadDocument({
    //     documentName: name,
    //     type: "userStats",
    //   })
    //   break
    case "userStatus":
      // No need to await
      loadDocument({
        documentName: name,
        type: "userStatus",
      })
      break
    case "cluster":
      // No need to await
      loadDocument({
        documentName: name,
        type: "cluster",
      })
      break
    case "contacts":
      // No need to await
      loadMap({
        mapName: name,
        type: "contacts",
      })
      break
    // case "smsHistories":
    //   // No need to await
    //   loadMap({
    //     mapName: name,
    //     type: "smsHistories",
    //   })
    //   break
    default:
      break
  }
}
